import type { Passenger, DiscountCard } from '@/types/passengerTypes'

/**
 * Serialize passengers to the planner's compact URL format.
 * Format: TYPE:DISCOUNT_CODES:TRAVEL_PASSES
 *
 * Examples:
 * - ADT,CHD,CHD
 * - ADT:SKYSS,CHD:SKYSS
 * - ADT:SKYSS+STUDENT:PASS123,CHD
 */
export function serializePassengersToUrl(passengers: Passenger[]): string {
  return passengers
    .map((p) => {
      const parts = [p.type]

      const discountCodes = p.discount_cards?.map((dc) => dc.code).join('+') || ''
      parts.push(discountCodes)

      const travelPassCodes = p.travel_passes?.map((tp) => tp.code).join('+') || ''
      if (travelPassCodes) {
        parts.push(travelPassCodes)
      }

      // Join with : and remove trailing empty parts
      return parts.join(':').replace(/:+$/, '')
    })
    .join(',')
}

/**
 * Parse passengers from the compact URL format, reconstructing minimal
 * discount_cards / travel_passes from their codes.
 */
export function parsePassengersFromUrl(urlParam: string): Passenger[] {
  return urlParam.split(',').map((passengerStr, index) => {
    const [type, discountCodes = '', travelPassCodes = ''] = passengerStr.split(':')

    const discount_cards: DiscountCard[] = discountCodes
      ? discountCodes.split('+').map((code) => ({
          code,
          description: code,
          name: code,
          requires_passenger_identifier: false,
        }))
      : []

    const travel_passes = travelPassCodes
      ? travelPassCodes.split('+').map((code) => ({ code }))
      : []

    return {
      id: generatePassengerId(index),
      type,
      discount_cards,
      travel_passes,
    }
  })
}

/**
 * Standardized passenger ID generation. Returns "passenger_N" (1-indexed).
 */
export function generatePassengerId(index: number): string {
  return `passenger_${index + 1}`
}
